import { useEffect, useRef, useState } from 'react';
import { subMonths } from 'date-fns';

import type {
  BrandCode,
  CaptureState,
  PaymentSearchByField,
  PaymentSortField,
  PaymentSortState,
  PaymentTypeKey,
  UiPaymentStatus,
} from '@models/payments';
import { PaymentSearchByFields } from '@models/payments';
import type { GetPaymentsParams } from '@features/payments/api';
import { paymentTypeKeyToApi, uiPaymentStatusToApi } from '@features/payments/helpers';
import { usePaymentsQuery } from '@features/payments/queries';
import { useDebounce } from '@hooks/useDebounce';
import { useToast } from '@hooks/useToast';
import { formatYmd } from '@utils/date';

export type StatusFilter = UiPaymentStatus | 'all';
export type TypeFilter = PaymentTypeKey | 'all';
export type BrandCodeFilter = BrandCode | 'all';
export type CaptureStateFilter = CaptureState | 'all';
export type SearchByFilter = PaymentSearchByField | 'all';

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_SORT: PaymentSortState = { field: 'date', dir: 'desc' };

//uuid regex , to check if input is a valid uuid
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SEARCH_BY_REQUIRES_UUID: readonly PaymentSearchByField[] = [
  PaymentSearchByFields.PAYMENT_ID,
  PaymentSearchByFields.PAYMENT_SESSION_ID,
  PaymentSearchByFields.SUBSCRIPTION_ID,
  PaymentSearchByFields.CUSTOMER_ID,
];

export function usePaymentsList() {
  const [search, setSearchValue] = useState('');
  const [searchBy, setSearchByValue] = useState<SearchByFilter>('all');
  const [status, setStatusValue] = useState<StatusFilter>('all');
  const [type, setTypeValue] = useState<TypeFilter>('all');
  const [brandCode, setBrandCodeValue] = useState<BrandCodeFilter>('all');
  const [captureState, setCaptureStateValue] = useState<CaptureStateFilter>('all');
  const [fromDate, setFromDateValue] = useState<Date>(() => subMonths(new Date(), 1));
  const [toDate, setToDateValue] = useState<Date>(() => new Date());
  const [sort, setSort] = useState<PaymentSortState>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const debouncedSearch = useDebounce(search, 300);

  const trimmedTyped = search.trim();
  const trimmedDebounced = debouncedSearch.trim();
  const searchByPicked = searchBy !== 'all';
  const requiresUuid = searchBy !== 'all' && SEARCH_BY_REQUIRES_UUID.includes(searchBy);

  const debouncedUuidValid = !requiresUuid || UUID_REGEX.test(trimmedDebounced);
  const typedUuidValid = !requiresUuid || UUID_REGEX.test(trimmedTyped);

  const searchActive = trimmedDebounced.length > 0 && searchByPicked && debouncedUuidValid;

  const queryParams: GetPaymentsParams = {
    page: page + 1,
    limit: rowsPerPage,
    fromDate: formatYmd(fromDate),
    toDate: formatYmd(toDate),
    status: status === 'all' ? undefined : uiPaymentStatusToApi(status),
    paymentType: type === 'all' ? undefined : paymentTypeKeyToApi(type),
    brandCode: brandCode === 'all' ? undefined : brandCode,
    searchQuery: searchActive ? trimmedDebounced : undefined,
    searchBy: searchActive ? searchBy : undefined,
    captureState: captureState === 'all' ? undefined : captureState,
    sortBy: sort.field,
    sortOrder: sort.dir,
  };

  let searchError: string | null = null;
  if (trimmedTyped.length > 0 && !searchByPicked) {
    searchError = 'Please select search by field';
  } else if (trimmedTyped.length > 0 && !typedUuidValid) {
    searchError = 'Search query must be a valid UUID';
  }

  const { data, isLoading, isFetching, isError } = usePaymentsQuery(queryParams);
  const toast = useToast();

  const errorShownRef = useRef(false);
  useEffect(() => {
    if (isError && !errorShownRef.current) {
      toast.error('Failed to load payments');
      errorShownRef.current = true;
    }
    if (!isError) errorShownRef.current = false;
  }, [isError, toast]);

  const pageCount = data?.meta.pageCount;
  useEffect(() => {
    if (pageCount !== undefined && pageCount > 0 && page >= pageCount) {
      setPage(0);
    }
  }, [page, pageCount]);

  const setSearch = (v: string) => {
    setSearchValue(v);
    setPage(0);
  };

  const setSearchBy = (v: SearchByFilter) => {
    setSearchByValue(v);
    setPage(0);
  };

  const setStatus = (v: StatusFilter) => {
    setStatusValue(v);
    setPage(0);
  };

  const setType = (v: TypeFilter) => {
    setTypeValue(v);
    setPage(0);
  };

  const setBrandCode = (v: BrandCodeFilter) => {
    setBrandCodeValue(v);
    setPage(0);
  };

  const setCaptureState = (v: CaptureStateFilter) => {
    setCaptureStateValue(v);
    setPage(0);
  };

  const setFromDate = (d: Date) => {
    setFromDateValue(d);
    setPage(0);
  };

  const setToDate = (d: Date) => {
    setToDateValue(d);
    setPage(0);
  };

  const handleSort = (field: PaymentSortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'desc' },
    );
    setPage(0);
  };

  const clearFilters = () => {
    setStatusValue('all');
    setTypeValue('all');
    setBrandCodeValue('all');
    setCaptureStateValue('all');
    setPage(0);
  };

  return {
    search,
    setSearch,
    searchBy,
    setSearchBy,
    searchError,
    status,
    setStatus,
    type,
    setType,
    brandCode,
    setBrandCode,
    captureState,
    setCaptureState,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    sort,
    handleSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    clearFilters,
    rows: data?.data ?? [],
    total: data?.meta.itemCount ?? 0,
    isLoading,
    isFetching,
  };
}
